import React, { useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllShapes } from "http-hooks/shape/useShape";

const SelectShapes = ({
  value,
  onChange,
  placeholder = "Search shapes...",
  isDisabled = false,
  isRequired = false,
  label = "Shape",
  className = "",
  filters = {},
  showLabel = true,
  isMulti = false,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllShapes,
    data: { data: allShapes } = [],
  } = useAllShapes({
    shapetype: "",
    ag_status: "",
    noPagination: true,
    ...filters,
  });

  // Convert shapes to options for Select component - memoized for performance
  const shapeOptions = useMemo(() => allShapes?.map(shape => ({
    value: shape.shapeId,
    label: shape.shapetype,
    shapetype: shape.shapetype,
    ...shape
  })) || [], [allShapes]);

  // Auto-select the matching option when shapes load and value is provided as simple object
  useEffect(() => {
    // If value is provided as a simple object with shapetype but not as a full option
    if (value && !value.value && value.shapetype && shapeOptions.length > 0 && onChange) {
      const matchedOption = shapeOptions.find(option => 
        String(option.shapetype) === String(value.shapetype)
      );
      
      if (matchedOption && matchedOption !== value) {
        onChange(isMulti ? [matchedOption] : matchedOption);
      }
    }
  }, [value, shapeOptions, onChange, isMulti]);

  const handleAddNew = () => {
    openModal('shape');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new shape"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={shapeOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllShapes}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
        isMulti={isMulti}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new shape"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectShapes; 